import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Upload, X } from "lucide-react";

interface LogoUploadProps {
  label: string;
  description?: string;
  currentUrl?: string;
  onUploadComplete: (url: string) => void;
  onRemove: () => void;
  imageWidth?: number;
}

export const LogoUpload = ({
  label,
  description,
  currentUrl,
  onUploadComplete,
  onRemove,
  imageWidth = 80,
}: LogoUploadProps) => {
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      toast.error("Formato non valido. Usa JPG, PNG o SVG.");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 2MB.");
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${label.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("platform-logos")
        .upload(fileName, file, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("platform-logos")
        .getPublicUrl(fileName);

      onUploadComplete(data.publicUrl);
      toast.success("Logo caricato con successo!");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Errore durante l'upload del logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!currentUrl) return;

    try {
      const fileName = currentUrl.split("/").pop();
      if (fileName) {
        await supabase.storage.from("platform-logos").remove([fileName]);
      }
      onRemove();
      toast.success("Logo rimosso con successo!");
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Errore durante la rimozione del logo");
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-base font-bold">{label}</Label>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </div>

      {currentUrl ? (
        <div className="flex flex-col items-start gap-3">
          <div className="relative inline-block">
            <img
              src={currentUrl}
              alt={label}
              style={{ width: `${imageWidth}px`, height: 'auto' }}
              className="border-2 border-dark bg-background p-2"
            />
          </div>
          <BrutalButton
            type="button"
            variant="danger"
            size="sm"
            onClick={handleRemove}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            Rimuovi Logo
          </BrutalButton>
        </div>
      ) : (
        <div className="relative">
          <input
            type="file"
            accept="image/jpeg,image/png,image/svg+xml"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
            id={`logo-upload-${label}`}
          />
          <label htmlFor={`logo-upload-${label}`} className="cursor-pointer inline-block w-full">
            <div className="px-4 py-3 border-[3px] border-dark bg-card hover:bg-surface transition-colors font-heading font-bold flex items-center justify-center gap-2">
              <Upload className="h-4 w-4" />
              {uploading ? "Caricamento..." : "Carica Logo"}
            </div>
          </label>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Formati: JPG, PNG, SVG • Max 2MB
      </p>
    </div>
  );
};
